import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { MappedEvidencePage } from '../../types/project';

interface EvidencePageChipsProps {
  mappedPages: MappedEvidencePage[];
  onPageClick: (documentId: string, pageNumber: number, documentName: string) => void;
  onViewAllClick: () => void;
}

const EvidencePageChips: React.FC<EvidencePageChipsProps> = ({
  mappedPages,
  onPageClick,
  onViewAllClick
}) => {
  if (!mappedPages || mappedPages.length === 0) {
    return null;
  }

  // Group pages by document
  const pagesByDocument = mappedPages.reduce((acc, page) => {
    if (!acc[page.document_id]) {
      acc[page.document_id] = {
        document_name: page.document_name,
        pages: []
      };
    }
    acc[page.document_id].pages.push(page);
    return acc;
  }, {} as Record<string, { document_name: string; pages: MappedEvidencePage[] }>);

  const documentCount = Object.keys(pagesByDocument).length;
  const totalPageCount = mappedPages.length;

  // Show first 3 pages (across all documents)
  const displayPages = mappedPages.slice(0, 3);
  const remainingCount = totalPageCount - 3;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 500, color: 'text.secondary' }}
      >
        Evidence found on {totalPageCount} page{totalPageCount !== 1 ? 's' : ''} across {documentCount} document{documentCount !== 1 ? 's' : ''}:
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Individual page chips */}
        {displayPages.map((page, index) => (
          <Chip
            key={`${page.document_id}-${page.page_number}-${index}`}
            icon={<DocumentIcon sx={{ fontSize: 16 }} />}
            label={`${page.document_name} p${page.page_number}`}
            onClick={() => onPageClick(page.document_id, page.page_number, page.document_name)}
            clickable
            size="small"
            color="primary"
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'primary.50',
              }
            }}
          />
        ))}

        {/* "More pages" indicator */}
        {remainingCount > 0 && (
          <Chip
            label={`+${remainingCount} more`}
            onClick={onViewAllClick}
            clickable
            size="small"
            color="secondary"
            variant="outlined"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'secondary.50',
              }
            }}
          />
        )}

        {/* View All button */}
        <Button
          size="small"
          variant="text"
          startIcon={<VisibilityIcon />}
          onClick={onViewAllClick}
          sx={{
            ml: 1,
            textTransform: 'none',
            fontSize: '0.875rem',
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.50',
            }
          }}
        >
          View All
        </Button>
      </Box>
    </Box>
  );
};

export default EvidencePageChips;
